import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Users,
  Package,
  Wrench,
  Plus,
  ArrowLeft,
  Calendar,
  Upload,
  Loader2,
  Phone,
  Mail,
  Clock,
  FileText,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Box,
  AlertCircle,
  TrendingDown,
  DollarSign,
  CalendarClock,
  Shield,
  Clipboard,
  RefreshCw,
  Settings,
  History,
  Minus,
  PlusCircle,
  User,
  Briefcase,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPLOYMENT_TYPES = [
  { code: 'full_time', name: 'Full Time' },
  { code: 'part_time', name: 'Part Time' },
  { code: 'contract', name: 'Contract' },
  { code: 'temporary', name: 'Temporary' },
];

const LEAVE_TYPES = [
  { code: 'annual', name: 'Annual Leave' },
  { code: 'sick', name: 'Sick Leave' },
  { code: 'family', name: 'Family Responsibility' },
  { code: 'unpaid', name: 'Unpaid Leave' },
  { code: 'other', name: 'Other' },
];

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const INVENTORY_CATEGORIES = [
  { code: 'cleaning', name: 'Cleaning Supplies' },
  { code: 'garden', name: 'Garden Supplies' },
  { code: 'electrical', name: 'Electrical' },
  { code: 'plumbing', name: 'Plumbing' },
  { code: 'safety', name: 'Safety Equipment' },
  { code: 'office', name: 'Office Supplies' },
  { code: 'tools', name: 'Tools' },
  { code: 'other', name: 'Other' },
];

const ASSET_CATEGORIES = [
  { code: 'garden_equipment', name: 'Garden Equipment' },
  { code: 'pool_equipment', name: 'Pool Equipment' },
  { code: 'cleaning_equipment', name: 'Cleaning Equipment' },
  { code: 'power_tools', name: 'Power Tools' },
  { code: 'generators', name: 'Generators' },
  { code: 'pumps', name: 'Pumps' },
  { code: 'security', name: 'Security Equipment' },
  { code: 'furniture', name: 'Furniture' },
  { code: 'vehicles', name: 'Vehicles' },
  { code: 'other', name: 'Other' },
];

const ASSET_CONDITIONS = [
  { code: 'excellent', name: 'Excellent', color: 'bg-emerald-100 text-emerald-700' },
  { code: 'good', name: 'Good', color: 'bg-green-100 text-green-700' },
  { code: 'fair', name: 'Fair', color: 'bg-yellow-100 text-yellow-700' },
  { code: 'poor', name: 'Poor', color: 'bg-orange-100 text-orange-700' },
  { code: 'needs_repair', name: 'Needs Repair', color: 'bg-red-100 text-red-700' },
];

export default function Operations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('staff');
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  
  // Data
  const [staff, setStaff] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [assets, setAssets] = useState([]);
  
  // Dialogs
  const [showStaffDialog, setShowStaffDialog] = useState(false);
  const [showStaffDetailDialog, setShowStaffDetailDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showShiftDialog, setShowShiftDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [showInventoryDetailDialog, setShowInventoryDetailDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [showAssetDialog, setShowAssetDialog] = useState(false);
  const [showAssetDetailDialog, setShowAssetDetailDialog] = useState(false);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  
  // Selected items
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Forms
  const [staffForm, setStaffForm] = useState({
    name: '', job_title: '', contact_number: '', email: '', start_date: '',
    employment_type: 'full_time', emergency_contact_name: '', emergency_contact_number: '',
    hourly_rate: '', notes: ''
  });
  
  const [leaveForm, setLeaveForm] = useState({
    staff_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: ''
  });
  
  const [shiftForm, setShiftForm] = useState({
    staff_id: '', title: '', day_of_week: 0, start_time: '', end_time: '', tasks: ''
  });
  
  const [inventoryForm, setInventoryForm] = useState({
    item_name: '', category: 'other', quantity: 0, minimum_stock_level: 5,
    unit: 'units', supplier_name: '', supplier_contact: '', unit_cost: '',
    storage_location: '', notes: ''
  });
  
  const [issueForm, setIssueForm] = useState({ quantity: 1, staff_id: '', reason: '' });
  const [receiveForm, setReceiveForm] = useState({ quantity: 1, reason: '' });
  
  const [assetForm, setAssetForm] = useState({
    asset_name: '', category: 'other', description: '', purchase_date: '',
    purchase_value: '', supplier: '', serial_number: '', model_number: '',
    warranty_expiry_date: '', service_interval_days: '', last_service_date: '',
    location: '', condition: 'good', notes: ''
  });
  
  const [maintenanceForm, setMaintenanceForm] = useState({
    service_date: '', service_type: 'routine', service_provider: '',
    maintenance_cost: '', description: '', parts_replaced: '',
    next_service_date: '', notes: ''
  });
  
  const fileInputRef = useRef(null);

  const isTrustee = user?.role === 'trustee' || user?.is_trustee === true || 
                   user?.role === 'property_manager' || user?.role === 'platform_owner' ||
                   user?.role === 'managing_agent' || user?.role === 'agent_admin';

  useEffect(() => {
    if (isTrustee) fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsRes = await fetch(`${API_URL}/api/operations/dashboard/stats`, {
        credentials: 'include'
      });
      if (statsRes.ok) setStats(await statsRes.json());
      
      // Fetch alerts
      const alertsRes = await fetch(`${API_URL}/api/operations/alerts`, {
        credentials: 'include'
      });
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      
      // Fetch based on active tab
      if (activeTab === 'staff' || activeTab === 'roster') {
        const staffRes = await fetch(`${API_URL}/api/operations/staff/list`, {
          credentials: 'include'
        });
        if (staffRes.ok) setStaff(await staffRes.json());
        
        const leavesRes = await fetch(`${API_URL}/api/operations/leave/list`, {
          credentials: 'include'
        });
        if (leavesRes.ok) setLeaves(await leavesRes.json());
        
        const shiftsRes = await fetch(`${API_URL}/api/operations/shifts/list`, {
          credentials: 'include'
        });
        if (shiftsRes.ok) setShifts(await shiftsRes.json());
      }
      
      if (activeTab === 'inventory') {
        const invRes = await fetch(`${API_URL}/api/operations/inventory/list`, {
          credentials: 'include'
        });
        if (invRes.ok) setInventory(await invRes.json());
      }
      
      if (activeTab === 'assets') {
        const assetRes = await fetch(`${API_URL}/api/operations/assets/list`, {
          credentials: 'include'
        });
        if (assetRes.ok) setAssets(await assetRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `R${parseFloat(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;
  };

  // ==================== STAFF HANDLERS ====================
  const handleCreateStaff = async () => {
    if (!staffForm.name || !staffForm.job_title || !staffForm.contact_number) {
      toast.error('Name, job title, and contact number are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...staffForm,
        hourly_rate: staffForm.hourly_rate ? parseFloat(staffForm.hourly_rate) : null
      };
      
      const res = await fetch(`${API_URL}/api/operations/staff/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Staff member added');
        setShowStaffDialog(false);
        setStaffForm({
          name: '', job_title: '', contact_number: '', email: '', start_date: '',
          employment_type: 'full_time', emergency_contact_name: '', emergency_contact_number: '',
          hourly_rate: '', notes: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add staff');
      }
    } catch (error) {
      toast.error('Failed to add staff');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewStaff = async (staffMember) => {
    try {
      const res = await fetch(`${API_URL}/api/operations/staff/${staffMember.staff_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        setSelectedStaff(await res.json());
        setShowStaffDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load staff details');
    }
  };

  const handleRequestLeave = async () => {
    if (!leaveForm.staff_id || !leaveForm.start_date || !leaveForm.end_date) {
      toast.error('Staff member and dates are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/leave/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(leaveForm)
      });
      
      if (res.ok) {
        toast.success('Leave request created');
        setShowLeaveDialog(false);
        setLeaveForm({ staff_id: '', leave_type: 'annual', start_date: '', end_date: '', reason: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproveLeave = async (leaveId) => {
    try {
      const res = await fetch(`${API_URL}/api/operations/leave/${leaveId}/approve`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        toast.success('Leave approved');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleRejectLeave = async (leaveId) => {
    try {
      const res = await fetch(`${API_URL}/api/operations/leave/${leaveId}/reject`, {
        method: 'POST', credentials: 'include'
      });
      if (res.ok) {
        toast.success('Leave rejected');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  const handleCreateShift = async () => {
    if (!shiftForm.staff_id || !shiftForm.title || !shiftForm.start_time || !shiftForm.end_time) {
      toast.error('All fields are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/operations/shifts/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(shiftForm)
      });
      
      if (res.ok) {
        toast.success('Shift added');
        setShowShiftDialog(false);
        setShiftForm({ staff_id: '', title: '', day_of_week: 0, start_time: '', end_time: '', tasks: '' });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to create shift');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== INVENTORY HANDLERS ====================
  const handleCreateInventory = async () => {
    if (!inventoryForm.item_name) {
      toast.error('Item name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...inventoryForm,
        quantity: parseInt(inventoryForm.quantity) || 0,
        minimum_stock_level: parseInt(inventoryForm.minimum_stock_level) || 5,
        unit_cost: inventoryForm.unit_cost ? parseFloat(inventoryForm.unit_cost) : null
      };
      
      const res = await fetch(`${API_URL}/api/operations/inventory/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Inventory item added');
        setShowInventoryDialog(false);
        setInventoryForm({
          item_name: '', category: 'other', quantity: 0, minimum_stock_level: 5,
          unit: 'units', supplier_name: '', supplier_contact: '', unit_cost: '',
          storage_location: '', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add inventory item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewInventory = async (item) => {
    try {
      const res = await fetch(`${API_URL}/api/operations/inventory/${item.item_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        setSelectedInventory(await res.json());
        setShowInventoryDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load item details');
    }
  };

  const handleIssueStock = async () => {
    if (!issueForm.quantity || issueForm.quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        quantity: issueForm.quantity,
        ...(issueForm.staff_id && { staff_id: issueForm.staff_id }),
        ...(issueForm.reason && { reason: issueForm.reason })
      });
      
      const res = await fetch(`${API_URL}/api/operations/inventory/${selectedInventory.item_id}/issue?${params}`, {
        method: 'POST', credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Stock issued');
        setShowIssueDialog(false);
        setIssueForm({ quantity: 1, staff_id: '', reason: '' });
        handleViewInventory(selectedInventory);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to issue stock');
      }
    } catch (error) {
      toast.error('Failed to issue stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReceiveStock = async () => {
    if (!receiveForm.quantity || receiveForm.quantity < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }
    
    setSubmitting(true);
    try {
      const params = new URLSearchParams({
        quantity: receiveForm.quantity,
        ...(receiveForm.reason && { reason: receiveForm.reason })
      });
      
      const res = await fetch(`${API_URL}/api/operations/inventory/${selectedInventory.item_id}/receive?${params}`, {
        method: 'POST', credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Stock received');
        setShowReceiveDialog(false);
        setReceiveForm({ quantity: 1, reason: '' });
        handleViewInventory(selectedInventory);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to receive stock');
    } finally {
      setSubmitting(false);
    }
  };

  // ==================== ASSET HANDLERS ====================
  const handleCreateAsset = async () => {
    if (!assetForm.asset_name) {
      toast.error('Asset name is required');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        ...assetForm,
        purchase_value: assetForm.purchase_value ? parseFloat(assetForm.purchase_value) : null,
        service_interval_days: assetForm.service_interval_days ? parseInt(assetForm.service_interval_days) : null
      };
      
      const res = await fetch(`${API_URL}/api/operations/assets/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Asset added');
        setShowAssetDialog(false);
        setAssetForm({
          asset_name: '', category: 'other', description: '', purchase_date: '',
          purchase_value: '', supplier: '', serial_number: '', model_number: '',
          warranty_expiry_date: '', service_interval_days: '', last_service_date: '',
          location: '', condition: 'good', notes: ''
        });
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add asset');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewAsset = async (asset) => {
    try {
      const res = await fetch(`${API_URL}/api/operations/assets/${asset.asset_id}`, {
        credentials: 'include'
      });
      if (res.ok) {
        setSelectedAsset(await res.json());
        setShowAssetDetailDialog(true);
      }
    } catch (error) {
      toast.error('Failed to load asset details');
    }
  };

  const handleAddMaintenance = async () => {
    if (!maintenanceForm.service_date || !maintenanceForm.description) {
      toast.error('Service date and description are required');
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        asset_id: selectedAsset.asset_id,
        ...maintenanceForm,
        maintenance_cost: maintenanceForm.maintenance_cost ? parseFloat(maintenanceForm.maintenance_cost) : null
      };
      
      const res = await fetch(`${API_URL}/api/operations/assets/${selectedAsset.asset_id}/maintenance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Maintenance record added');
        setShowMaintenanceDialog(false);
        setMaintenanceForm({
          service_date: '', service_type: 'routine', service_provider: '',
          maintenance_cost: '', description: '', parts_replaced: '',
          next_service_date: '', notes: ''
        });
        handleViewAsset(selectedAsset);
      }
    } catch (error) {
      toast.error('Failed to add maintenance record');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isTrustee) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-500 mb-4">
              The Operations module is only available to trustees and managing agents.
            </p>
            <Button onClick={() => navigate(-1)}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 lg:p-8" data-testid="operations-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" className="mb-2 -ml-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-2">
                <Settings className="w-8 h-8 text-teal-600" />
                Operations Management
              </h1>
              <p className="text-slate-500 mt-1">Manage staff, inventory, and assets</p>
            </div>
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <Card className="border-teal-200 bg-teal-50">
              <CardContent className="p-4">
                <p className="text-sm text-teal-600 font-medium">Staff</p>
                <p className="text-2xl font-bold text-teal-700">{stats.staff.total}</p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-sm text-amber-600 font-medium">Pending Leave</p>
                <p className="text-2xl font-bold text-amber-700">{stats.staff.pending_leave}</p>
              </CardContent>
            </Card>
            <Card className={stats.inventory.low_stock > 0 ? "border-red-200 bg-red-50" : ""}>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500 font-medium">Low Stock Items</p>
                <p className={`text-2xl font-bold ${stats.inventory.low_stock > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                  {stats.inventory.low_stock}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500 font-medium">Total Assets</p>
                <p className="text-2xl font-bold text-slate-900">{stats.assets.total}</p>
              </CardContent>
            </Card>
            <Card className={stats.assets.needs_repair > 0 ? "border-orange-200 bg-orange-50" : ""}>
              <CardContent className="p-4">
                <p className="text-sm text-slate-500 font-medium">Needs Repair</p>
                <p className={`text-2xl font-bold ${stats.assets.needs_repair > 0 ? 'text-orange-600' : 'text-slate-900'}`}>
                  {stats.assets.needs_repair}
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Alerts Banner */}
        {alerts.length > 0 && (
          <Card className="mb-6 border-amber-300 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-900">{alerts.length} Alert{alerts.length > 1 ? 's' : ''}</span>
              </div>
              <div className="space-y-1">
                {alerts.slice(0, 3).map((alert, i) => (
                  <p key={i} className="text-sm text-amber-800">{alert.title}: {alert.message}</p>
                ))}
                {alerts.length > 3 && (
                  <p className="text-sm text-amber-600">+{alerts.length - 3} more alerts</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="staff" data-testid="tab-staff">
              <Users className="w-4 h-4 mr-2" />
              Staff
            </TabsTrigger>
            <TabsTrigger value="roster" data-testid="tab-roster">
              <Calendar className="w-4 h-4 mr-2" />
              Duty Roster
            </TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory">
              <Package className="w-4 h-4 mr-2" />
              Inventory
            </TabsTrigger>
            <TabsTrigger value="assets" data-testid="tab-assets">
              <Wrench className="w-4 h-4 mr-2" />
              Assets
            </TabsTrigger>
          </TabsList>

          {/* STAFF TAB */}
          <TabsContent value="staff" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Staff Members</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowLeaveDialog(true)}>
                  <Calendar className="w-4 h-4 mr-2" />
                  Request Leave
                </Button>
                <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowStaffDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Staff
                </Button>
              </div>
            </div>
            
            {loading ? (
              <Card><CardContent className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></CardContent></Card>
            ) : staff.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Staff Members</h3>
                  <p className="text-slate-500 mb-4">Add your first staff member to get started</p>
                  <Button onClick={() => setShowStaffDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Staff
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {staff.map(member => (
                  <Card key={member.staff_id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleViewStaff(member)}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-slate-900">{member.name}</h3>
                          <p className="text-sm text-slate-500">{member.job_title}</p>
                        </div>
                        <Badge variant="outline">{member.employment_type.replace('_', ' ')}</Badge>
                      </div>
                      <div className="mt-3 space-y-1 text-sm text-slate-600">
                        <p className="flex items-center gap-2"><Phone className="w-4 h-4" />{member.contact_number}</p>
                        {member.email && <p className="flex items-center gap-2"><Mail className="w-4 h-4" />{member.email}</p>}
                        <p className="flex items-center gap-2"><Calendar className="w-4 h-4" />Started: {formatDate(member.start_date)}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Pending Leave Requests */}
            {leaves.filter(l => l.status === 'pending').length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-500" />
                    Pending Leave Requests
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {leaves.filter(l => l.status === 'pending').map(leave => (
                      <div key={leave.leave_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="font-medium">{leave.staff_name}</p>
                          <p className="text-sm text-slate-600">
                            {leave.leave_type} leave: {formatDate(leave.start_date)} - {formatDate(leave.end_date)}
                          </p>
                          {leave.reason && <p className="text-sm text-slate-500">{leave.reason}</p>}
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleRejectLeave(leave.leave_id)}>
                            <XCircle className="w-4 h-4" />
                          </Button>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleApproveLeave(leave.leave_id)}>
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* DUTY ROSTER TAB */}
          <TabsContent value="roster" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Weekly Duty Roster</h2>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowShiftDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Shift
              </Button>
            </div>
            
            <Card>
              <CardContent className="p-4">
                {shifts.length === 0 ? (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No shifts scheduled. Add a shift to create the duty roster.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-2">
                    {DAYS_OF_WEEK.map((day, index) => (
                      <div key={day} className="min-h-[150px]">
                        <p className="font-semibold text-sm text-slate-700 mb-2 text-center">{day}</p>
                        <div className="space-y-1">
                          {shifts.filter(s => s.day_of_week === index).map(shift => (
                            <div key={shift.shift_id} className="p-2 bg-teal-50 rounded text-xs">
                              <p className="font-medium">{shift.staff_name}</p>
                              <p className="text-slate-600">{shift.start_time} - {shift.end_time}</p>
                              <p className="text-slate-500">{shift.title}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* INVENTORY TAB */}
          <TabsContent value="inventory" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Inventory / Stock Room</h2>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowInventoryDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
            
            {loading ? (
              <Card><CardContent className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></CardContent></Card>
            ) : inventory.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Inventory Items</h3>
                  <p className="text-slate-500 mb-4">Add your first inventory item</p>
                  <Button onClick={() => setShowInventoryDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Item
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {inventory.map(item => (
                  <Card 
                    key={item.item_id} 
                    className={`hover:shadow-md transition-shadow cursor-pointer ${item.low_stock_alert ? 'border-red-300 bg-red-50' : ''}`}
                    onClick={() => handleViewInventory(item)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-slate-900">{item.item_name}</h3>
                          <p className="text-sm text-slate-500">{INVENTORY_CATEGORIES.find(c => c.code === item.category)?.name}</p>
                        </div>
                        {item.low_stock_alert && (
                          <Badge className="bg-red-500 text-white">Low Stock</Badge>
                        )}
                      </div>
                      <div className="mt-3">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Stock Level</span>
                          <span className={item.low_stock_alert ? 'text-red-600 font-semibold' : ''}>
                            {item.quantity} {item.unit}
                          </span>
                        </div>
                        <Progress 
                          value={Math.min((item.quantity / item.minimum_stock_level) * 100, 100)} 
                          className={`h-2 ${item.low_stock_alert ? 'bg-red-200' : ''}`}
                        />
                        <p className="text-xs text-slate-500 mt-1">Min: {item.minimum_stock_level} {item.unit}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ASSETS TAB */}
          <TabsContent value="assets" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold">Asset Register</h2>
              <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => setShowAssetDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Asset
              </Button>
            </div>
            
            {loading ? (
              <Card><CardContent className="p-8 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" /></CardContent></Card>
            ) : assets.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Wrench className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No Assets</h3>
                  <p className="text-slate-500 mb-4">Add your first asset to the register</p>
                  <Button onClick={() => setShowAssetDialog(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Asset
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assets.map(asset => (
                  <Card 
                    key={asset.asset_id} 
                    className={`hover:shadow-md transition-shadow cursor-pointer ${asset.condition === 'needs_repair' ? 'border-orange-300' : ''}`}
                    onClick={() => handleViewAsset(asset)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-slate-900">{asset.asset_name}</h3>
                          <p className="text-sm text-slate-500">{ASSET_CATEGORIES.find(c => c.code === asset.category)?.name}</p>
                        </div>
                        <Badge className={ASSET_CONDITIONS.find(c => c.code === asset.condition)?.color}>
                          {ASSET_CONDITIONS.find(c => c.code === asset.condition)?.name}
                        </Badge>
                      </div>
                      <div className="mt-3 space-y-1 text-sm text-slate-600">
                        {asset.serial_number && <p>S/N: {asset.serial_number}</p>}
                        {asset.purchase_value && <p>Value: {formatCurrency(asset.purchase_value)}</p>}
                        {asset.warranty_expiry_date && (
                          <p className={new Date(asset.warranty_expiry_date) < new Date() ? 'text-red-600' : ''}>
                            Warranty: {formatDate(asset.warranty_expiry_date)}
                          </p>
                        )}
                        {asset.next_service_date && (
                          <p>Next Service: {formatDate(asset.next_service_date)}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ADD STAFF DIALOG */}
      <Dialog open={showStaffDialog} onOpenChange={setShowStaffDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Staff Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={staffForm.name} onChange={(e) => setStaffForm({...staffForm, name: e.target.value})} placeholder="John Smith" />
              </div>
              <div className="space-y-2">
                <Label>Job Title *</Label>
                <Input value={staffForm.job_title} onChange={(e) => setStaffForm({...staffForm, job_title: e.target.value})} placeholder="Gardener" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact Number *</Label>
                <Input value={staffForm.contact_number} onChange={(e) => setStaffForm({...staffForm, contact_number: e.target.value})} placeholder="012 345 6789" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={staffForm.email} onChange={(e) => setStaffForm({...staffForm, email: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input type="date" value={staffForm.start_date} onChange={(e) => setStaffForm({...staffForm, start_date: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Employment Type</Label>
                <Select value={staffForm.employment_type} onValueChange={(v) => setStaffForm({...staffForm, employment_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Emergency Contact Name</Label>
                <Input value={staffForm.emergency_contact_name} onChange={(e) => setStaffForm({...staffForm, emergency_contact_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Emergency Contact Number</Label>
                <Input value={staffForm.emergency_contact_number} onChange={(e) => setStaffForm({...staffForm, emergency_contact_number: e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea value={staffForm.notes} onChange={(e) => setStaffForm({...staffForm, notes: e.target.value})} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateStaff} disabled={submitting} className="bg-teal-600 hover:bg-teal-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Add Staff
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* STAFF DETAIL DIALOG */}
      <Dialog open={showStaffDetailDialog} onOpenChange={setShowStaffDetailDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedStaff?.name}</DialogTitle>
          </DialogHeader>
          {selectedStaff && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Job Title:</span><p className="font-medium">{selectedStaff.job_title}</p></div>
                <div><span className="text-slate-500">Employment:</span><p className="font-medium">{selectedStaff.employment_type.replace('_', ' ')}</p></div>
                <div><span className="text-slate-500">Contact:</span><p className="font-medium">{selectedStaff.contact_number}</p></div>
                <div><span className="text-slate-500">Email:</span><p className="font-medium">{selectedStaff.email || '-'}</p></div>
                <div><span className="text-slate-500">Start Date:</span><p className="font-medium">{formatDate(selectedStaff.start_date)}</p></div>
                <div><span className="text-slate-500">Emergency Contact:</span><p className="font-medium">{selectedStaff.emergency_contact_name || '-'} {selectedStaff.emergency_contact_number}</p></div>
              </div>
              {selectedStaff.documents?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Documents</Label>
                  <div className="space-y-2">
                    {selectedStaff.documents.map((doc, i) => (
                      <a key={i} href={`${API_URL}${doc.url}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-2 bg-slate-100 rounded hover:bg-slate-200 text-sm">
                        <FileText className="w-4 h-4" />
                        {doc.name} ({doc.type})
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffDetailDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LEAVE REQUEST DIALOG */}
      <Dialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Request Leave</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Staff Member *</Label>
              <Select value={leaveForm.staff_id} onValueChange={(v) => setLeaveForm({...leaveForm, staff_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select staff..." /></SelectTrigger>
                <SelectContent>
                  {staff.map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Leave Type</Label>
              <Select value={leaveForm.leave_type} onValueChange={(v) => setLeaveForm({...leaveForm, leave_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LEAVE_TYPES.map(t => <SelectItem key={t.code} value={t.code}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Date *</Label><Input type="date" value={leaveForm.start_date} onChange={(e) => setLeaveForm({...leaveForm, start_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>End Date *</Label><Input type="date" value={leaveForm.end_date} onChange={(e) => setLeaveForm({...leaveForm, end_date: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Reason</Label><Textarea value={leaveForm.reason} onChange={(e) => setLeaveForm({...leaveForm, reason: e.target.value})} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveDialog(false)}>Cancel</Button>
            <Button onClick={handleRequestLeave} disabled={submitting}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SHIFT DIALOG */}
      <Dialog open={showShiftDialog} onOpenChange={setShowShiftDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Shift</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Staff Member *</Label>
              <Select value={shiftForm.staff_id} onValueChange={(v) => setShiftForm({...shiftForm, staff_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select staff..." /></SelectTrigger>
                <SelectContent>
                  {staff.map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Shift Title *</Label><Input value={shiftForm.title} onChange={(e) => setShiftForm({...shiftForm, title: e.target.value})} placeholder="Morning Cleaning" /></div>
            <div className="space-y-2">
              <Label>Day of Week</Label>
              <Select value={String(shiftForm.day_of_week)} onValueChange={(v) => setShiftForm({...shiftForm, day_of_week: parseInt(v)})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DAYS_OF_WEEK.map((day, i) => <SelectItem key={i} value={String(i)}>{day}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Start Time *</Label><Input type="time" value={shiftForm.start_time} onChange={(e) => setShiftForm({...shiftForm, start_time: e.target.value})} /></div>
              <div className="space-y-2"><Label>End Time *</Label><Input type="time" value={shiftForm.end_time} onChange={(e) => setShiftForm({...shiftForm, end_time: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Tasks</Label><Textarea value={shiftForm.tasks} onChange={(e) => setShiftForm({...shiftForm, tasks: e.target.value})} rows={2} placeholder="List tasks for this shift..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowShiftDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateShift} disabled={submitting}>Add Shift</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD INVENTORY DIALOG */}
      <Dialog open={showInventoryDialog} onOpenChange={setShowInventoryDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Inventory Item</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Item Name *</Label><Input value={inventoryForm.item_name} onChange={(e) => setInventoryForm({...inventoryForm, item_name: e.target.value})} placeholder="Bleach" /></div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={inventoryForm.category} onValueChange={(v) => setInventoryForm({...inventoryForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INVENTORY_CATEGORIES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Quantity</Label><Input type="number" value={inventoryForm.quantity} onChange={(e) => setInventoryForm({...inventoryForm, quantity: e.target.value})} /></div>
              <div className="space-y-2"><Label>Min Level</Label><Input type="number" value={inventoryForm.minimum_stock_level} onChange={(e) => setInventoryForm({...inventoryForm, minimum_stock_level: e.target.value})} /></div>
              <div className="space-y-2"><Label>Unit</Label><Input value={inventoryForm.unit} onChange={(e) => setInventoryForm({...inventoryForm, unit: e.target.value})} placeholder="units" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Supplier Name</Label><Input value={inventoryForm.supplier_name} onChange={(e) => setInventoryForm({...inventoryForm, supplier_name: e.target.value})} /></div>
              <div className="space-y-2"><Label>Unit Cost (R)</Label><Input type="number" step="0.01" value={inventoryForm.unit_cost} onChange={(e) => setInventoryForm({...inventoryForm, unit_cost: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Storage Location</Label><Input value={inventoryForm.storage_location} onChange={(e) => setInventoryForm({...inventoryForm, storage_location: e.target.value})} placeholder="Storeroom A" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInventoryDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateInventory} disabled={submitting} className="bg-teal-600 hover:bg-teal-700">Add Item</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INVENTORY DETAIL DIALOG */}
      <Dialog open={showInventoryDetailDialog} onOpenChange={setShowInventoryDetailDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedInventory?.item_name}</DialogTitle></DialogHeader>
          {selectedInventory && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setShowInventoryDetailDialog(false); setShowIssueDialog(true); }}>
                  <Minus className="w-4 h-4 mr-2" />Issue Stock
                </Button>
                <Button variant="outline" className="flex-1" onClick={() => { setShowInventoryDetailDialog(false); setShowReceiveDialog(true); }}>
                  <PlusCircle className="w-4 h-4 mr-2" />Receive Stock
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Category:</span><p className="font-medium">{INVENTORY_CATEGORIES.find(c => c.code === selectedInventory.category)?.name}</p></div>
                <div><span className="text-slate-500">Current Stock:</span><p className={`font-medium ${selectedInventory.low_stock_alert ? 'text-red-600' : ''}`}>{selectedInventory.quantity} {selectedInventory.unit}</p></div>
                <div><span className="text-slate-500">Min Level:</span><p className="font-medium">{selectedInventory.minimum_stock_level} {selectedInventory.unit}</p></div>
                <div><span className="text-slate-500">Unit Cost:</span><p className="font-medium">{formatCurrency(selectedInventory.unit_cost)}</p></div>
                <div><span className="text-slate-500">Supplier:</span><p className="font-medium">{selectedInventory.supplier_name || '-'}</p></div>
                <div><span className="text-slate-500">Location:</span><p className="font-medium">{selectedInventory.storage_location || '-'}</p></div>
              </div>
              {selectedInventory.movements?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Movement History</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedInventory.movements.map((mov, i) => (
                      <div key={i} className={`p-2 rounded text-sm ${mov.movement_type === 'issue' ? 'bg-red-50' : 'bg-green-50'}`}>
                        <div className="flex justify-between">
                          <span className="font-medium">{mov.movement_type === 'issue' ? '-' : '+'}{mov.quantity} {selectedInventory.unit}</span>
                          <span className="text-slate-500">{formatDate(mov.created_at)}</span>
                        </div>
                        {mov.staff_name && <p className="text-xs text-slate-600">By: {mov.staff_name}</p>}
                        {mov.reason && <p className="text-xs text-slate-500">{mov.reason}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowInventoryDetailDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ISSUE STOCK DIALOG */}
      <Dialog open={showIssueDialog} onOpenChange={setShowIssueDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Issue Stock</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Quantity *</Label><Input type="number" min="1" value={issueForm.quantity} onChange={(e) => setIssueForm({...issueForm, quantity: parseInt(e.target.value)})} /></div>
            <div className="space-y-2">
              <Label>Staff Member</Label>
              <Select value={issueForm.staff_id} onValueChange={(v) => setIssueForm({...issueForm, staff_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select staff..." /></SelectTrigger>
                <SelectContent>
                  {staff.map(s => <SelectItem key={s.staff_id} value={s.staff_id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Reason</Label><Input value={issueForm.reason} onChange={(e) => setIssueForm({...issueForm, reason: e.target.value})} placeholder="Daily cleaning supplies" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowIssueDialog(false)}>Cancel</Button>
            <Button onClick={handleIssueStock} disabled={submitting} className="bg-red-600 hover:bg-red-700">Issue Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RECEIVE STOCK DIALOG */}
      <Dialog open={showReceiveDialog} onOpenChange={setShowReceiveDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Receive Stock</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Quantity *</Label><Input type="number" min="1" value={receiveForm.quantity} onChange={(e) => setReceiveForm({...receiveForm, quantity: parseInt(e.target.value)})} /></div>
            <div className="space-y-2"><Label>Reason/PO Number</Label><Input value={receiveForm.reason} onChange={(e) => setReceiveForm({...receiveForm, reason: e.target.value})} placeholder="PO-12345" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReceiveDialog(false)}>Cancel</Button>
            <Button onClick={handleReceiveStock} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700">Receive Stock</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD ASSET DIALOG */}
      <Dialog open={showAssetDialog} onOpenChange={setShowAssetDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Asset</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Asset Name *</Label><Input value={assetForm.asset_name} onChange={(e) => setAssetForm({...assetForm, asset_name: e.target.value})} placeholder="Lawnmower" /></div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={assetForm.category} onValueChange={(v) => setAssetForm({...assetForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSET_CATEGORIES.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={assetForm.description} onChange={(e) => setAssetForm({...assetForm, description: e.target.value})} rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Serial Number</Label><Input value={assetForm.serial_number} onChange={(e) => setAssetForm({...assetForm, serial_number: e.target.value})} /></div>
              <div className="space-y-2"><Label>Model Number</Label><Input value={assetForm.model_number} onChange={(e) => setAssetForm({...assetForm, model_number: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Purchase Date</Label><Input type="date" value={assetForm.purchase_date} onChange={(e) => setAssetForm({...assetForm, purchase_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Purchase Value (R)</Label><Input type="number" step="0.01" value={assetForm.purchase_value} onChange={(e) => setAssetForm({...assetForm, purchase_value: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Supplier</Label><Input value={assetForm.supplier} onChange={(e) => setAssetForm({...assetForm, supplier: e.target.value})} /></div>
              <div className="space-y-2"><Label>Location</Label><Input value={assetForm.location} onChange={(e) => setAssetForm({...assetForm, location: e.target.value})} placeholder="Garden Shed" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Warranty Expiry</Label><Input type="date" value={assetForm.warranty_expiry_date} onChange={(e) => setAssetForm({...assetForm, warranty_expiry_date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Service Interval (days)</Label><Input type="number" value={assetForm.service_interval_days} onChange={(e) => setAssetForm({...assetForm, service_interval_days: e.target.value})} placeholder="90" /></div>
            </div>
            <div className="space-y-2">
              <Label>Condition</Label>
              <Select value={assetForm.condition} onValueChange={(v) => setAssetForm({...assetForm, condition: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSET_CONDITIONS.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssetDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateAsset} disabled={submitting} className="bg-teal-600 hover:bg-teal-700">Add Asset</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ASSET DETAIL DIALOG */}
      <Dialog open={showAssetDetailDialog} onOpenChange={setShowAssetDetailDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedAsset?.asset_name}</DialogTitle></DialogHeader>
          {selectedAsset && (
            <div className="space-y-4">
              <Button variant="outline" className="w-full" onClick={() => { setShowAssetDetailDialog(false); setShowMaintenanceDialog(true); }}>
                <Wrench className="w-4 h-4 mr-2" />Add Maintenance Record
              </Button>
              <Badge className={ASSET_CONDITIONS.find(c => c.code === selectedAsset.condition)?.color}>
                {ASSET_CONDITIONS.find(c => c.code === selectedAsset.condition)?.name}
              </Badge>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Category:</span><p className="font-medium">{ASSET_CATEGORIES.find(c => c.code === selectedAsset.category)?.name}</p></div>
                <div><span className="text-slate-500">Serial:</span><p className="font-medium">{selectedAsset.serial_number || '-'}</p></div>
                <div><span className="text-slate-500">Model:</span><p className="font-medium">{selectedAsset.model_number || '-'}</p></div>
                <div><span className="text-slate-500">Location:</span><p className="font-medium">{selectedAsset.location || '-'}</p></div>
                <div><span className="text-slate-500">Purchase Value:</span><p className="font-medium">{formatCurrency(selectedAsset.purchase_value)}</p></div>
                <div><span className="text-slate-500">Warranty:</span><p className={`font-medium ${selectedAsset.warranty_expiry_date && new Date(selectedAsset.warranty_expiry_date) < new Date() ? 'text-red-600' : ''}`}>{formatDate(selectedAsset.warranty_expiry_date)}</p></div>
                <div><span className="text-slate-500">Last Service:</span><p className="font-medium">{formatDate(selectedAsset.last_service_date)}</p></div>
                <div><span className="text-slate-500">Next Service:</span><p className="font-medium">{formatDate(selectedAsset.next_service_date)}</p></div>
              </div>
              {selectedAsset.maintenance_log?.length > 0 && (
                <div>
                  <Label className="mb-2 block">Maintenance History</Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedAsset.maintenance_log.map((log, i) => (
                      <div key={i} className="p-3 bg-slate-50 rounded">
                        <div className="flex justify-between">
                          <span className="font-medium">{log.service_type}</span>
                          <span className="text-sm text-slate-500">{formatDate(log.service_date)}</span>
                        </div>
                        <p className="text-sm text-slate-600">{log.description}</p>
                        {log.service_provider && <p className="text-xs text-slate-500">By: {log.service_provider}</p>}
                        {log.maintenance_cost && <p className="text-xs text-slate-500">Cost: {formatCurrency(log.maintenance_cost)}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setShowAssetDetailDialog(false)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADD MAINTENANCE DIALOG */}
      <Dialog open={showMaintenanceDialog} onOpenChange={setShowMaintenanceDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Maintenance Record</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Service Date *</Label><Input type="date" value={maintenanceForm.service_date} onChange={(e) => setMaintenanceForm({...maintenanceForm, service_date: e.target.value})} /></div>
              <div className="space-y-2">
                <Label>Service Type</Label>
                <Select value={maintenanceForm.service_type} onValueChange={(v) => setMaintenanceForm({...maintenanceForm, service_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine Service</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="replacement">Parts Replacement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Service Provider</Label><Input value={maintenanceForm.service_provider} onChange={(e) => setMaintenanceForm({...maintenanceForm, service_provider: e.target.value})} /></div>
              <div className="space-y-2"><Label>Cost (R)</Label><Input type="number" step="0.01" value={maintenanceForm.maintenance_cost} onChange={(e) => setMaintenanceForm({...maintenanceForm, maintenance_cost: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Description *</Label><Textarea value={maintenanceForm.description} onChange={(e) => setMaintenanceForm({...maintenanceForm, description: e.target.value})} rows={2} /></div>
            <div className="space-y-2"><Label>Parts Replaced</Label><Input value={maintenanceForm.parts_replaced} onChange={(e) => setMaintenanceForm({...maintenanceForm, parts_replaced: e.target.value})} /></div>
            <div className="space-y-2"><Label>Next Service Date</Label><Input type="date" value={maintenanceForm.next_service_date} onChange={(e) => setMaintenanceForm({...maintenanceForm, next_service_date: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMaintenanceDialog(false)}>Cancel</Button>
            <Button onClick={handleAddMaintenance} disabled={submitting}>Add Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
